import http from "@/helper/http";
import { RESOURCE_CACHE } from "@/config/queryConfig";

// Request deduplication cache
const pendingRequests = new Map();

// Data cache with timestamps
const dataCache = new Map();

// Expose cache for clearing (used by useCacheClear composable)
if (typeof window !== "undefined") {
  window.__VUEX_GROUPS_CACHE__ = dataCache;
}

const state = {
  // Store Groups (groups that a store belongs to)
  storeGroups: [],
  loadingStoreGroups: false,

  // Group Stores (stores that belong to a group)
  groupStores: [],
  loadingGroupStores: false,

  // Error handling
  error: null,
};

const getters = {
  // Store Groups
  storeGroups: (state) => state.storeGroups,
  loadingStoreGroups: (state) => state.loadingStoreGroups,

  // Group Stores
  groupStores: (state) => state.groupStores,
  loadingGroupStores: (state) => state.loadingGroupStores,

  // Errors
  error: (state) => state.error,
};

const mutations = {
  // Store Groups
  SET_STORE_GROUPS(state, groups) {
    state.storeGroups = groups;
  },
  SET_LOADING_STORE_GROUPS(state, loading) {
    state.loadingStoreGroups = loading;
  },

  // Group Stores
  SET_GROUP_STORES(state, stores) {
    state.groupStores = stores;
  },
  SET_LOADING_GROUP_STORES(state, loading) {
    state.loadingGroupStores = loading;
  },

  // Error handling
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
};

const actions = {
  // ==================== STORE GROUPS ====================

  async fetchStoreGroups({ commit }, { storeId, forceRefresh = false } = {}) {
    const cacheKey = `store-groups-${storeId}`;

    // Check cache first (unless forceRefresh)
    console.log(`🔍 Checking cache for store groups key: ${cacheKey}`);
    console.log(
      `📊 Cache size: ${dataCache.size}, Force refresh: ${forceRefresh}`
    );

    if (!forceRefresh && dataCache.has(cacheKey)) {
      const cached = dataCache.get(cacheKey);
      const age = Date.now() - cached.timestamp;
      const maxAge = RESOURCE_CACHE.groups.staleTime;

      console.log(
        `⏰ Store groups cache age: ${Math.round(
          age / 1000
        )}s, Max age: ${Math.round(maxAge / 1000)}s`
      );

      if (age < maxAge) {
        console.log(
          `✅ Using cached store groups (${Math.round(
            age / 1000
          )}s old) - No loading state`
        );
        // Don't set loading state for cached data
        commit("SET_STORE_GROUPS", cached.data.items || []);
        return cached.data;
      } else {
        console.log(
          `♻️ Store groups cache expired (${Math.round(
            age / 1000
          )}s old), fetching fresh data`
        );
        dataCache.delete(cacheKey);
      }
    } else {
      console.log(`❌ Store groups cache miss for key: ${cacheKey}`);
    }

    // Check if already fetching (deduplication)
    if (pendingRequests.has(cacheKey)) {
      console.log("⏭️ Deduplicating store groups request");
      return pendingRequests.get(cacheKey);
    }

    // Only set loading state when actually fetching
    commit("SET_LOADING_STORE_GROUPS", true);
    commit("CLEAR_ERROR");

    const requestPromise = (async () => {
      try {
        const response = await http.get(`/v2/stores/${storeId}/groups`, {
          params: { per_page: 100 },
        });

        const data = response.data || {};

        // Cache the result
        console.log(`💾 Caching store groups data for key: ${cacheKey}`);
        dataCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });
        console.log(`📊 Cache size after setting: ${dataCache.size}`);

        commit("SET_STORE_GROUPS", data.items || []);
        return data;
      } catch (error) {
        const message =
          error?.response?.data?.error?.message ||
          "Failed to load store groups";
        commit("SET_ERROR", message);
        throw error;
      } finally {
        commit("SET_LOADING_STORE_GROUPS", false);
        // Clean up pending request
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store the promise for deduplication
    pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  },

  // ==================== GROUP STORES ====================

  async fetchGroupStores({ commit }, { groupId, forceRefresh = false } = {}) {
    const cacheKey = `group-stores-${groupId}`;

    // Check cache first (unless forceRefresh)
    console.log(`🔍 Checking cache for group stores key: ${cacheKey}`);
    console.log(
      `📊 Cache size: ${dataCache.size}, Force refresh: ${forceRefresh}`
    );

    if (!forceRefresh && dataCache.has(cacheKey)) {
      const cached = dataCache.get(cacheKey);
      const age = Date.now() - cached.timestamp;
      const maxAge = RESOURCE_CACHE.groups.staleTime;

      console.log(
        `⏰ Group stores cache age: ${Math.round(
          age / 1000
        )}s, Max age: ${Math.round(maxAge / 1000)}s`
      );

      if (age < maxAge) {
        console.log(
          `✅ Using cached group stores (${Math.round(
            age / 1000
          )}s old) - No loading state`
        );
        // Don't set loading state for cached data
        commit("SET_GROUP_STORES", cached.data.items || []);
        return cached.data;
      } else {
        console.log(
          `♻️ Group stores cache expired (${Math.round(
            age / 1000
          )}s old), fetching fresh data`
        );
        dataCache.delete(cacheKey);
      }
    } else {
      console.log(`❌ Group stores cache miss for key: ${cacheKey}`);
    }

    // Check if already fetching (deduplication)
    if (pendingRequests.has(cacheKey)) {
      console.log("⏭️ Deduplicating group stores request");
      return pendingRequests.get(cacheKey);
    }

    // Only set loading state when actually fetching
    commit("SET_LOADING_GROUP_STORES", true);
    commit("CLEAR_ERROR");

    const requestPromise = (async () => {
      try {
        const response = await http.get(`/v2/groups/${groupId}/stores`, {
          params: { per_page: 100 },
        });

        const data = response.data || {};

        // Cache the result
        console.log(`💾 Caching group stores data for key: ${cacheKey}`);
        dataCache.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });
        console.log(`📊 Cache size after setting: ${dataCache.size}`);

        commit("SET_GROUP_STORES", data.items || []);
        return data;
      } catch (error) {
        const message =
          error?.response?.data?.error?.message ||
          "Failed to load group stores";
        commit("SET_ERROR", message);
        throw error;
      } finally {
        commit("SET_LOADING_GROUP_STORES", false);
        // Clean up pending request
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store the promise for deduplication
    pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  },

  // Clear cache
  clearGroupsCache() {
    dataCache.clear();
    pendingRequests.clear();
    console.log("🗑️ Groups cache cleared");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
